import Bugsnag from '@bugsnag/js';
import firebase from 'firebase/app';
import 'firebase/firestore';
import { useEffect, useMemo } from 'react';
import { useDocument } from 'react-firebase-hooks/firestore';
import { useUserIsAuthenticated } from '../../../app/hooks/useUserIsAuthenticated';
import { Collections } from '../collections';
import { formatDocument } from '../documentConverter';

const getFirestoreDocumentReference = (
  userIsAuthenticated: boolean,
  requireAuthentication: boolean,
  collection: Collections,
  documentId?: string,
): firebase.firestore.DocumentReference | undefined => {
  if (requireAuthentication && !userIsAuthenticated) {
    return undefined;
  }

  if (!documentId) {
    return undefined;
  }

  return firebase.firestore().collection(collection).doc(documentId);
};

// To return loading state
export function useDocumentSnapshot<T>(
  collection: Collections,
  documentId: string | undefined,
  requireAuthentication: boolean,
  returnLoading: true,
): [T | undefined, boolean];

// To not break old implementations
export function useDocumentSnapshot<T>(
  collection: Collections,
  documentId?: string,
  requireAuthentication?: boolean,
): T | undefined;

export function useDocumentSnapshot<T extends firebase.firestore.DocumentData>(
  collection: Collections,
  documentId?: string,
  requireAuthentication = true,
  returnLoading?: boolean,
): T | undefined | [T | undefined, boolean] {
  const userIsAuthenticated = useUserIsAuthenticated();

  const [documentSnapshot, loading, error] = useDocument(
    getFirestoreDocumentReference(userIsAuthenticated, requireAuthentication, collection, documentId),
  );

  useEffect(() => {
    if (error) {
      Bugsnag.notify(error, (event) => {
        // eslint-disable-next-line no-param-reassign
        event.context = `Firestore useDocumentSnapshot Error ${collection}`;
        // eslint-disable-next-line no-param-reassign
        event.groupingHash = `useDocumentSnapshot.${collection}`;
        event.addMetadata('query', { collection, documentId });
      });
    }
  }, [error, collection, documentId]);

  const formattedDocument = useMemo(() => {
    if (documentSnapshot && !error && !loading) {
      return formatDocument<T>(documentSnapshot);
    }

    return undefined;
  }, [documentSnapshot, loading, error]);

  /* istanbul ignore next */
  if (returnLoading) {
    return [formattedDocument, loading];
  }

  return formattedDocument;
}
